/* BP-022 · Buyer Intelligence Page — Session 1 shell.
   List of buyers with 7-factor Buyer Score + a drawer with 4 tabs:
   Overview · Buy Box · Funding · Activity. Session 2 will add AI Review,
   Offers, Transactions, Matches, Audit. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, UserPlus, Filter, RefreshCw, X, DollarSign, Activity,
  FileText, Users, Shield, TrendingUp, ChevronRight, Search,
  BadgeCheck, AlertCircle, Building2, Plus,
  Sparkles, ThumbsUp, ThumbsDown, Camera, Bookmark, RotateCw,
} from "lucide-react";


const TIER_TONE = {
  elite:   { text: "#065f46", bg: "#a7f3d0", border: "#059669" },
  strong:  { text: "#047857", bg: "#d1fae5", border: "#10b981" },
  watch:   { text: "#b45309", bg: "#fef3c7", border: "#f59e0b" },
  dormant: { text: "#64748b", bg: "#f1f5f9", border: "#94a3b8" },
};

const FUNDING_TONE = {
  verified: { text: "#065f46", bg: "#d1fae5", icon: BadgeCheck },
  pending:  { text: "#b45309", bg: "#fef3c7", icon: AlertCircle },
  expired:  { text: "#b91c1c", bg: "#fee2e2", icon: AlertCircle },
  unknown:  { text: "#64748b", bg: "#f1f5f9", icon: AlertCircle },
};

const STATUS_LABEL = {
  active:   "Active",  warming: "Warming", inactive: "Inactive",
  blocked:  "Blocked", archived: "Archived",
};

function fmt$(n) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(v)) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}


export default function BuyerIntelligencePage() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [tierFilter, setTierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tierFilter)   params.tier   = tierFilter;
      if (statusFilter) params.status = statusFilter;
      if (query)        params.q      = query;
      const { data } = await api.get("/v3/buyers", { params });
      setBuyers(data || []);
    } catch { toast.error("Failed to load buyers"); }
    finally { setLoading(false); }
  }, [query, tierFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    const total = buyers.length;
    const active = buyers.filter((b) => b.status === "active").length;
    const verified = buyers.filter((b) => b.funding_status === "verified").length;
    const withScore = buyers.filter((b) => b.buyer_score !== undefined
                                              && b.buyer_score !== null);
    const avgScore = withScore.length
      ? Math.round(withScore.reduce((s, b) => s + (b.buyer_score || 0), 0)
          / withScore.length)
      : null;
    return { total, active, verified, avgScore };
  }, [buyers]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24"
          data-testid="buyer-intelligence-page">
      <div className="max-w-[1400px] mx-auto px-4 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-amber-600">
              Buyer Intelligence · BP-022
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">
              Buyer Intelligence
            </h1>
            <div className="text-sm text-slate-500">
              7-factor deterministic scoring · activity feed · funding readiness
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              disabled={loading}
              data-testid="buyers-refresh-btn"
              className="text-sm px-3 py-2 rounded-md border border-slate-200
                          bg-white hover:bg-slate-50 flex items-center gap-1.5
                          disabled:opacity-50"
            >
              {loading
                ? <Loader2 size={14} className="animate-spin"/>
                : <RefreshCw size={14}/>}
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(true)}
              data-testid="buyers-new-btn"
              className="text-sm px-3 py-2 rounded-md bg-amber-500 text-white
                          hover:bg-amber-600 flex items-center gap-1.5"
            >
              <UserPlus size={14}/> New Buyer
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid gap-3 md:grid-cols-4 mb-4"
              data-testid="buyers-kpi-row">
          {[
            { label: "Total Buyers", value: kpis.total,
              testid: "kpi-total", icon: Users, color: "slate" },
            { label: "Active",       value: kpis.active,
              testid: "kpi-active", icon: TrendingUp, color: "emerald" },
            { label: "Verified Funding", value: kpis.verified,
              testid: "kpi-verified", icon: BadgeCheck, color: "green" },
            { label: "Avg Buyer Score",
              value: kpis.avgScore !== null ? kpis.avgScore : "—",
              testid: "kpi-avg-score", icon: Shield, color: "violet" },
          ].map((k) => (
            <div key={k.label} data-testid={k.testid}
                  className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider
                                    font-semibold text-slate-500">
                  {k.label}
                </div>
                <k.icon size={16} className={`text-${k.color}-500`}/>
              </div>
              <div className="text-3xl font-bold text-slate-900 tabular-nums
                                  mt-2">
                {k.value}
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-3 flex-wrap"
              data-testid="buyers-filters">
          <div className="flex-1 min-w-[240px] relative">
            <Search size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2
                                    text-slate-400"/>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="buyers-search-input"
              placeholder="Search by name, email, company…"
              className="w-full pl-9 pr-3 py-2 rounded-md text-sm
                          border border-slate-200 bg-white
                          focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            data-testid="buyers-tier-filter"
            className="text-sm px-3 py-2 rounded-md border border-slate-200 bg-white"
          >
            <option value="">All tiers</option>
            {["elite", "strong", "watch", "dormant"].map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="buyers-status-filter"
            className="text-sm px-3 py-2 rounded-md border border-slate-200 bg-white"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_LABEL).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>

        {/* Buyer list */}
        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 size={20} className="animate-spin text-slate-400"/>
          </div>
        ) : buyers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300
                            bg-white p-12 text-center"
                data-testid="buyers-empty">
            <Users className="mx-auto text-slate-300 mb-3" size={40}/>
            <div className="text-sm text-slate-500">
              No buyers yet. Click <b>New Buyer</b> to get started.
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
                data-testid="buyers-table">
            <div className="grid grid-cols-[minmax(0,1.6fr)_100px_120px_140px_120px_20px]
                              gap-3 px-4 py-2.5 text-[10px] uppercase
                              tracking-wider font-semibold text-slate-500
                              border-b border-slate-100 bg-slate-50/70">
              <div>Buyer</div><div>Score</div><div>Tier</div>
              <div>Funding</div><div>Status</div><div></div>
            </div>
            {buyers.map((b) => (
              <BuyerRow key={b.id} buyer={b}
                          onClick={() => setSelected(b)}/>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateBuyerModal
          onClose={() => setShowCreate(false)}
          onCreated={(nb) => {
            setShowCreate(false);
            setSelected(nb);
            load();
          }}
        />
      )}

      {selected && (
        <BuyerDrawer buyer={selected}
                      onClose={() => { setSelected(null); load(); }}
                      onChanged={load}/>
      )}
    </div>
  );
}


function BuyerRow({ buyer, onClick }) {
  const tt = TIER_TONE[buyer.buyer_tier] || TIER_TONE.dormant;
  const ft = FUNDING_TONE[buyer.funding_status] || FUNDING_TONE.unknown;
  const FI = ft.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`buyer-row-${buyer.id}`}
      className="grid grid-cols-[minmax(0,1.6fr)_100px_120px_140px_120px_20px]
                  gap-3 px-4 py-3 items-center text-left w-full
                  border-t border-slate-50 hover:bg-amber-50/40 transition"
    >
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900 truncate">
          {buyer.display_name || buyer.name || "—"}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {(buyer.company_name || buyer.company) && (
            <><Building2 size={10} className="inline mr-1"/>
              {buyer.company_name || buyer.company} · </>
          )}
          {buyer.primary_email || buyer.email || "no email"}
        </div>
      </div>
      <div className="text-xl font-bold tabular-nums text-slate-900">
        {buyer.buyer_score ?? "—"}
      </div>
      <div>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full
                          ring-1"
              style={{ color: tt.text, background: tt.bg, borderColor: tt.border }}>
          {buyer.buyer_tier || "unscored"}
        </span>
      </div>
      <div className="text-xs">
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
              style={{ color: ft.text, background: ft.bg }}>
          <FI size={10}/> {buyer.funding_status || "unknown"}
        </span>
      </div>
      <div className="text-xs text-slate-600">
        {STATUS_LABEL[buyer.status] || "—"}
      </div>
      <ChevronRight size={16} className="text-slate-300"/>
    </button>
  );
}


function CreateBuyerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    display_name: "", primary_email: "", primary_phone: "",
    company_name: "", buyer_type: "cash_buyer",
    price_min: "", price_max: "", cash_capacity: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.display_name.trim())
      return toast.error("Display name is required");
    setSaving(true);
    try {
      const body = { ...form };
      ["price_min", "price_max", "cash_capacity"].forEach((k) => {
        body[k] = form[k] ? parseFloat(form[k]) : undefined;
      });
      const { data } = await api.post("/v3/buyers", body);
      toast.success(`Buyer created — ${data.display_name}`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50
                      flex items-center justify-center p-4"
          data-testid="buyer-create-modal"
          onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg
                        p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-lg font-semibold text-slate-900">New Buyer</div>
          <button onClick={onClose}
                    data-testid="buyer-create-close"
                    className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <div className="grid gap-3 grid-cols-2">
          <label className="col-span-2 text-xs text-slate-600">
            Display Name *
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              data-testid="buyer-create-name"
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"
              placeholder="Sarah Cash"
            />
          </label>
          <label className="text-xs text-slate-600">
            Email
            <input
              value={form.primary_email}
              onChange={(e) => setForm({ ...form, primary_email: e.target.value })}
              data-testid="buyer-create-email"
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"
              placeholder="sarah@example.com"
            />
          </label>
          <label className="text-xs text-slate-600">
            Phone
            <input
              value={form.primary_phone}
              onChange={(e) => setForm({ ...form, primary_phone: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"
              placeholder="+1-555-…"
            />
          </label>
          <label className="text-xs text-slate-600">
            Company
            <input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"
            />
          </label>
          <label className="text-xs text-slate-600">
            Buyer Type
            <select
              value={form.buyer_type}
              onChange={(e) => setForm({ ...form, buyer_type: e.target.value })}
              data-testid="buyer-create-type"
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200 bg-white"
            >
              {["cash_buyer", "landlord", "flipper", "institution",
                 "creative_finance", "agent", "other"].map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            Price Min
            <input type="number"
              value={form.price_min}
              onChange={(e) => setForm({ ...form, price_min: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200" placeholder="80000"/>
          </label>
          <label className="text-xs text-slate-600">
            Price Max
            <input type="number"
              value={form.price_max}
              onChange={(e) => setForm({ ...form, price_max: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200" placeholder="300000"/>
          </label>
          <label className="text-xs text-slate-600 col-span-2">
            Cash Capacity
            <input type="number"
              value={form.cash_capacity}
              onChange={(e) => setForm({ ...form, cash_capacity: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200" placeholder="500000"/>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose}
                    className="text-sm px-3 py-2 rounded-md border
                                border-slate-200 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving}
                    data-testid="buyer-create-submit"
                    className="text-sm px-4 py-2 rounded-md bg-amber-500
                                text-white hover:bg-amber-600
                                disabled:opacity-50 flex items-center gap-1">
            {saving && <Loader2 size={13} className="animate-spin"/>}
            Create Buyer
          </button>
        </div>
      </div>
    </div>
  );
}


function BuyerDrawer({ buyer, onClose, onChanged }) {
  const [tab, setTab] = useState("overview");
  const [score, setScore] = useState(null);
  const [activity, setActivity] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [aiReview, setAiReview] = useState(null);
  const [aiMode, setAiMode] = useState("snapshot");
  const [aiLoading, setAiLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);

  const TABS = [
    { id: "overview",  label: "Overview" },
    { id: "buybox",    label: "Buy Box" },
    { id: "funding",   label: "Funding" },
    { id: "activity",  label: "Activity" },
    { id: "airev",     label: "AI Review" },
    { id: "audit",     label: "Audit" },
  ];

  const loadScore = useCallback(async () => {
    try {
      const { data } = await api.get(`/v3/buyers/${buyer.id}/score`);
      setScore(data);
    } catch (err) {
      if (err?.response?.status === 404) setScore(null);
    }
  }, [buyer.id]);

  const loadActivity = useCallback(async () => {
    try {
      const { data } = await api.get(`/v3/buyers/${buyer.id}/activity`);
      setActivity(data || []);
    } catch { /* silent */ }
  }, [buyer.id]);

  const loadAiReview = useCallback(async (mode) => {
    try {
      const { data } = await api.get(
        `/v3/buyers/${buyer.id}/ai-review/latest`, { params: { mode } });
      setAiReview(data);
    } catch (err) {
      if (err?.response?.status === 404) setAiReview(null);
    }
  }, [buyer.id]);

  const loadSnapshots = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/v3/buyers/${buyer.id}/snapshot-history`);
      setSnapshots(data || []);
    } catch { /* silent */ }
  }, [buyer.id]);

  useEffect(() => {
    loadScore();
    loadActivity();
  }, [loadScore, loadActivity]);

  useEffect(() => {
    if (tab === "airev") loadAiReview(aiMode);
    if (tab === "audit") loadSnapshots();
  }, [tab, aiMode, loadAiReview, loadSnapshots]);

  const generateAiReview = async (opts = {}) => {
    setAiLoading(true);
    try {
      const { data } = await api.post(
        `/v3/buyers/${buyer.id}/ai-review`,
        { mode: aiMode, force: !!opts.force });
      setAiReview(data);
      loadActivity();
      if (data.cached) toast("Loaded cached AI review");
      else toast.success("AI review generated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI review failed");
    } finally { setAiLoading(false); }
  };

  const sendAiFeedback = async (rating) => {
    if (!aiReview?.id) return;
    try {
      await api.post(
        `/v3/buyers/ai-review/${aiReview.id}/feedback`, { rating });
      toast.success("Feedback recorded");
    } catch { toast.error("Feedback failed"); }
  };

  const captureSnapshot = async () => {
    try {
      await api.post(
        `/v3/buyers/${buyer.id}/snapshot`, { reason: "manual" });
      toast.success("Snapshot captured");
      loadSnapshots();
      loadActivity();
    } catch { toast.error("Snapshot failed"); }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(
        `/v3/buyers/${buyer.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `buyer-${buyer.id}.pdf`;
      document.body.appendChild(a);
      a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
    finally { setDownloadingPdf(false); }
  };

  const recalc = async () => {
    setRecalculating(true);
    try {
      const { data } = await api.post(
        `/v3/buyers/${buyer.id}/score/recalculate`);
      setScore(data);
      loadActivity();
      onChanged();
      toast.success(`Score recalculated · ${data.score} (${data.tier})`);
    } catch { toast.error("Recalculation failed"); }
    finally { setRecalculating(false); }
  };

  const tt = TIER_TONE[score?.tier ?? buyer.buyer_tier] || TIER_TONE.dormant;

  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end"
          data-testid="buyer-drawer"
          onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto
                        shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-100 z-10 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-amber-600">
                {buyer.buyer_type?.replace(/_/g, " ") || "buyer"}
              </div>
              <div className="text-xl font-bold text-slate-900 mt-0.5"
                    data-testid="drawer-buyer-name">
                {buyer.display_name || buyer.name}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {buyer.primary_email || buyer.email}
                {(buyer.company_name || buyer.company) &&
                  <> · {buyer.company_name || buyer.company}</>}
              </div>
            </div>
            <button onClick={onClose}
                      data-testid="drawer-close"
                      className="text-slate-400 hover:text-slate-700">
              <X size={20}/></button>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <div>
              <div className="text-[9px] uppercase text-slate-400
                                tracking-wider font-semibold">Score</div>
              <div className="text-3xl font-bold tabular-nums"
                    style={{ color: tt.text }}
                    data-testid="drawer-buyer-score">
                {score?.score ?? buyer.buyer_score ?? "—"}
              </div>
            </div>
            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full
                              ring-1"
                    style={{ color: tt.text, background: tt.bg,
                              borderColor: tt.border }}
                    data-testid="drawer-buyer-tier">
              {score?.tier ?? buyer.buyer_tier ?? "unscored"}
            </span>
            <button onClick={recalc} disabled={recalculating}
                      data-testid="drawer-recalculate-btn"
                      className="ml-auto text-xs px-3 py-1.5 rounded-md
                                  bg-gradient-to-r from-amber-500 to-orange-500
                                  text-white hover:from-amber-600 hover:to-orange-600
                                  flex items-center gap-1 disabled:opacity-50">
              {recalculating
                ? <Loader2 size={12} className="animate-spin"/>
                : <RefreshCw size={12}/>}
              Recalculate
            </button>
            <button onClick={downloadPdf} disabled={downloadingPdf}
                      data-testid="drawer-pdf-btn"
                      title="Export PDF report"
                      className="text-xs px-2 py-1.5 rounded-md border
                                  border-slate-200 hover:bg-slate-50
                                  flex items-center gap-1 disabled:opacity-50">
              {downloadingPdf
                ? <Loader2 size={12} className="animate-spin"/>
                : <FileText size={12}/>}
              PDF
            </button>
          </div>
          <div className="flex gap-1 mt-4" data-testid="drawer-tabs">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`drawer-tab-${t.id}`}
                        className={`text-xs px-3 py-1.5 rounded-md transition
                          ${tab === t.id
                            ? "bg-amber-500 text-white"
                            : "text-slate-600 hover:bg-slate-100"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab body */}
        <div className="p-5 space-y-4">
          {tab === "overview" && (
            <OverviewTab buyer={buyer} score={score}/>
          )}
          {tab === "buybox"   && <BuyBoxTab buyer={buyer}/>}
          {tab === "funding"  && (
            <FundingTab buyer={buyer}
                          onRefresh={() => { onChanged(); loadScore(); }}/>
          )}
          {tab === "activity" && <ActivityTab activity={activity}/>}
          {tab === "airev"    && (
            <AIReviewTab
              review={aiReview}
              mode={aiMode}
              onModeChange={setAiMode}
              onGenerate={() => generateAiReview()}
              onForce={() => generateAiReview({ force: true })}
              onFeedback={sendAiFeedback}
              loading={aiLoading}
            />
          )}
          {tab === "audit"    && (
            <AuditTab
              snapshots={snapshots}
              onCapture={captureSnapshot}
            />
          )}
        </div>
      </div>
    </div>
  );
}


function OverviewTab({ buyer, score }) {
  return (
    <div className="space-y-4" data-testid="drawer-overview">
      <div className="grid gap-3 md:grid-cols-2">
        <FieldCard label="Price Range"
                     value={`${fmt$(buyer.price_min)} – ${fmt$(buyer.price_max)}`}
                     icon={DollarSign}/>
        <FieldCard label="Cash Capacity"
                     value={fmt$(buyer.cash_capacity)}
                     icon={Shield}/>
        <FieldCard label="Markets"
                     value={(buyer.preferred_markets || []).join(", ") || "—"}
                     icon={Users}/>
        <FieldCard label="Strategies"
                     value={(buyer.strategy_preferences || []).join(", ") || "—"}
                     icon={TrendingUp}/>
      </div>

      {score && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="drawer-score-breakdown">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">
              Score Breakdown · 7 factors
            </div>
            <div className="text-[10px] text-slate-400">
              {score.engine_version} · conf {(score.confidence * 100).toFixed(0)}%
            </div>
          </div>
          <div className="space-y-2">
            {(score.factors || []).map((f) => (
              <div key={f.key}
                    data-testid={`drawer-factor-${f.key}`}
                    className="flex items-center gap-3">
                <div className="w-40 text-xs text-slate-700">{f.label}</div>
                <div className="flex-1 h-2 bg-slate-100 rounded-full
                                    overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400
                                    to-orange-500"
                        style={{ width: `${f.score}%` }}/>
                </div>
                <div className="text-xs tabular-nums text-slate-500 w-14
                                  text-right">
                  {f.weight}% · {f.contribution.toFixed(1)}
                </div>
                <div className="w-10 text-sm font-bold tabular-nums text-right">
                  {f.score.toFixed(0)}
                </div>
              </div>
            ))}
          </div>
          {(score.data_gaps || []).length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider
                                font-semibold text-amber-700">
                Data gaps
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {score.data_gaps.join(" · ")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function FieldCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50/40 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase
                        tracking-wider text-slate-500 font-semibold">
        <Icon size={11}/> {label}
      </div>
      <div className="text-sm font-medium text-slate-800 mt-1 truncate">
        {value}
      </div>
    </div>
  );
}


function BuyBoxTab({ buyer }) {
  const [prefs, setPrefs] = useState(null);
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/v3/buyers/${buyer.id}/preferences/versions`);
        setVersions(data || []);
      } catch { /* silent */ }
    })();
  }, [buyer.id]);
  return (
    <div className="space-y-3" data-testid="drawer-buybox">
      <div className="text-xs text-slate-500">
        {versions.length > 0
          ? `${versions.length} archived version${versions.length > 1 ? "s" : ""}`
          : "No archived versions."}
        {" "}Buy-Box editing UI lands in Session 2 — for now use the API
        (POST /api/v3/buyers/{"{"}id{"}"}/preferences).
      </div>
      {versions.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold
                            text-slate-500 mb-2">Version history</div>
          <ul className="space-y-1.5 text-xs">
            {versions.map((v) => (
              <li key={v.id}
                    data-testid={`buybox-version-${v.version_number}`}
                    className="flex justify-between">
                <span>v{v.version_number}</span>
                <span className="text-slate-500">
                  {(v.archived_at || "").slice(0, 19)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function FundingTab({ buyer, onRefresh }) {
  const [form, setForm] = useState({
    status: buyer.funding_status || "pending",
    lender_name: "", lender_quality: "",
    cash_capacity: buyer.cash_capacity || "",
    expires_at: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      body.lender_quality = form.lender_quality
        ? parseFloat(form.lender_quality) : undefined;
      body.cash_capacity = form.cash_capacity
        ? parseFloat(form.cash_capacity) : undefined;
      if (!body.expires_at) delete body.expires_at;
      await api.post(`/v3/buyers/${buyer.id}/funding`, body);
      toast.success("Funding recorded");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="drawer-funding">
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <div className="grid gap-3 grid-cols-2">
          <label className="text-xs text-slate-600">
            Status
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="funding-status-select"
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200 bg-white">
              {["verified", "pending", "expired", "unknown"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            Lender Name
            <input value={form.lender_name}
              onChange={(e) => setForm({ ...form, lender_name: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"
              placeholder="Kiavi, Lima One, …"/>
          </label>
          <label className="text-xs text-slate-600">
            Lender Quality (0–1)
            <input type="number" min={0} max={1} step={0.1}
              value={form.lender_quality}
              onChange={(e) => setForm({ ...form, lender_quality: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200" placeholder="0.85"/>
          </label>
          <label className="text-xs text-slate-600">
            Cash Capacity
            <input type="number"
              value={form.cash_capacity}
              onChange={(e) => setForm({ ...form, cash_capacity: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200" placeholder="500000"/>
          </label>
          <label className="col-span-2 text-xs text-slate-600">
            POF Expires (ISO date)
            <input type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md text-sm
                          border border-slate-200"/>
          </label>
        </div>
        <button onClick={submit} disabled={saving}
                  data-testid="funding-submit-btn"
                  className="w-full text-sm px-4 py-2 rounded-md bg-emerald-500
                              text-white hover:bg-emerald-600
                              disabled:opacity-50 flex items-center gap-1
                              justify-center">
          {saving && <Loader2 size={13} className="animate-spin"/>}
          Record Funding Snapshot
        </button>
      </div>
    </div>
  );
}


function ActivityTab({ activity }) {
  if (activity.length === 0) {
    return (
      <div className="text-xs text-slate-400 italic py-8 text-center"
            data-testid="drawer-activity-empty">
        No activity logged yet.
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid="drawer-activity">
      {activity.map((a) => (
        <div key={a.id}
              data-testid={`activity-row-${a.id}`}
              className="flex items-baseline gap-3 text-xs border-l-2
                            border-slate-200 pl-3">
          <div className="tabular-nums text-slate-400 w-40">
            {(a.occurred_at || "").slice(0, 19)}
          </div>
          <div className="font-mono text-[10px] px-1.5 py-0.5 rounded
                            bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            {a.event_code}
          </div>
          {Object.keys(a.payload || {}).length > 0 && (
            <div className="text-slate-500 truncate">
              {JSON.stringify(a.payload)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


const AI_MODES = [
  { id: "snapshot",         label: "Snapshot" },
  { id: "next_best_action", label: "Next Action" },
  { id: "deal_fit",         label: "Deal Fit" },
  { id: "risk_flags",       label: "Risks" },
];


function AIReviewTab({ review, mode, onModeChange, onGenerate,
                          onForce, onFeedback, loading }) {
  return (
    <div className="space-y-3" data-testid="drawer-airev">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1" data-testid="airev-mode-tabs">
          {AI_MODES.map((m) => (
            <button key={m.id}
              onClick={() => onModeChange(m.id)}
              data-testid={`airev-mode-${m.id}`}
              className={`text-[11px] px-2 py-1 rounded-md ring-1 transition
                ${mode === m.id
                  ? "bg-violet-600 text-white ring-violet-600"
                  : "bg-white text-slate-600 ring-slate-200 hover:ring-violet-300"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onGenerate} disabled={loading}
            data-testid="airev-generate-btn"
            className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                        from-violet-500 to-fuchsia-500 text-white
                        hover:from-violet-600 hover:to-fuchsia-600
                        flex items-center gap-1 disabled:opacity-50">
            {loading
              ? <Loader2 size={13} className="animate-spin"/>
              : <Sparkles size={13}/>}
            {review ? "Refresh" : "Generate"}
          </button>
          {review && (
            <button
              onClick={onForce} disabled={loading}
              data-testid="airev-force-btn"
              title="Ignore cache"
              className="text-xs px-2 py-1.5 rounded-md border
                          border-slate-200 hover:bg-slate-50
                          disabled:opacity-50">
              <RotateCw size={13}/>
            </button>
          )}
        </div>
      </div>

      {!review && !loading && (
        <div className="text-xs text-slate-500 italic py-6 text-center"
              data-testid="airev-empty">
          No AI review for this mode yet — click Generate.
        </div>
      )}

      {review && (
        <div className="space-y-3">
          <div className="text-sm text-slate-800 font-medium leading-snug"
                data-testid="airev-headline">
            {review.headline}
            {review.cached && (
              <span className="ml-2 text-[10px] uppercase tracking-wider
                                px-2 py-0.5 rounded-full bg-slate-100
                                text-slate-500">cached</span>
            )}
            {typeof review.confidence === "number" && (
              <span className="ml-2 text-[10px] text-slate-500">
                conf {(review.confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>

          {(review.data_gaps || []).length > 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50
                              border border-amber-200 rounded px-2 py-1
                              inline-flex items-center gap-1"
                  data-testid="airev-gaps">
              <AlertCircle size={11}/>
              Data gaps: {review.data_gaps.join(", ")}
            </div>
          )}

          <div className="grid gap-2 md:grid-cols-3 text-xs">
            {[
              { key: "strengths",           title: "Strengths",     tone: "emerald" },
              { key: "opportunities",       title: "Opportunities", tone: "amber" },
              { key: "recommended_actions", title: "Actions",       tone: "violet" },
            ].map((sec) => (
              <div key={sec.key}
                    data-testid={`airev-section-${sec.key}`}
                    className={`rounded-lg border p-2.5 bg-${sec.tone}-50/40
                                border-${sec.tone}-200`}>
                <div className={`text-[10px] uppercase tracking-wider
                                  text-${sec.tone}-700 font-semibold mb-1`}>
                  {sec.title}
                </div>
                <ul className="space-y-1.5">
                  {(review[sec.key] || []).length === 0 && (
                    <li className="text-slate-400 italic">—</li>
                  )}
                  {(review[sec.key] || []).map((it, i) => (
                    <li key={i} className="text-slate-700 leading-snug">
                      {typeof it === "string" ? it : it.text}
                      {it.evidence_paths?.length > 0 && (
                        <div className="mt-0.5 flex flex-wrap gap-1">
                          {it.evidence_paths.slice(0, 2).map((p, j) => (
                            <span key={j}
                                  className="text-[9px] px-1.5 py-0
                                              rounded bg-slate-100 text-slate-500
                                              ring-1 ring-slate-200 font-mono">
                              {p}
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-[10px] text-slate-400">
              {review.model_name} · {review.prompt_version}
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => onFeedback("helpful")}
                data-testid="airev-fb-helpful"
                className="text-[11px] px-2 py-1 rounded border border-slate-200
                            hover:bg-emerald-50 hover:border-emerald-300
                            text-slate-600 flex items-center gap-1">
                <ThumbsUp size={11}/> Helpful
              </button>
              <button
                onClick={() => onFeedback("not_helpful")}
                data-testid="airev-fb-not-helpful"
                className="text-[11px] px-2 py-1 rounded border border-slate-200
                            hover:bg-red-50 hover:border-red-300
                            text-slate-600 flex items-center gap-1">
                <ThumbsDown size={11}/> Not Helpful
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function AuditTab({ snapshots, onCapture }) {
  return (
    <div className="space-y-3" data-testid="drawer-audit">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {snapshots.length > 0
            ? `${snapshots.length} snapshot${snapshots.length > 1 ? "s" : ""}`
            : "No snapshots yet."}
        </div>
        <button
          onClick={onCapture}
          data-testid="audit-capture-btn"
          className="text-xs px-3 py-1.5 rounded-md bg-slate-800 text-white
                      hover:bg-slate-900 flex items-center gap-1">
          <Camera size={12}/> Capture Snapshot
        </button>
      </div>
      {snapshots.length > 0 && (
        <ul className="space-y-1.5">
          {snapshots.map((s) => (
            <li key={s.id}
                data-testid={`audit-row-${s.id}`}
                className="flex items-baseline gap-3 text-xs border-l-2
                            border-slate-200 pl-3 py-1">
              <div className="tabular-nums text-slate-400 w-40">
                {(s.captured_at || "").slice(0, 19)}
              </div>
              <div className="font-mono text-[10px] px-1.5 py-0.5 rounded
                                bg-slate-100 text-slate-700 ring-1 ring-slate-200">
                {s.reason || "manual"}
              </div>
              <div className="text-slate-500">
                score {s.score?.score ?? "—"} ·
                tier {s.score?.tier ?? "—"} ·
                funding {s.funding?.status ?? "—"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

